import { useMemo } from "react";
import { getChainInfo, getNativeTokenDecimals } from "@/lib/chains";
import type { SimTokenInfoResponse } from "@/hooks/useSimTokenInfo";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import type { DescriptionFormatHints } from "@/utils/resolveDescriptionTemplates";

interface UseDescriptionFormatHintsParams {
  simTokenData: SimTokenInfoResponse | undefined;
  simTokenInData: SimTokenInfoResponse | undefined;
  simSellTokenData: SimTokenInfoResponse | undefined;
  tokenInAddress: string | undefined;
  tokenInChainId: number | undefined;
  isNativeOnlyAction: boolean;
  chainId: number | undefined;
}

/**
 * Computes DescriptionFormatHints from SimDune token data.
 *
 * Fallback chain:
 * 1. tokenInfo       (from payload.tokenAddress)
 * 2. tokenInInfo     (from payload.tokenIn, for swap actions)
 * 3. sellTokenInfo   (from payload.sellToken, for 0x/cow swap)
 * 4. Native default  (if tokenIn === "native", use 18 decimals + chain gas token)
 * 5. Native action   (if action is wrap-eth/unwrap-eth/send-native-token)
 * 6. undefined       (no hints available)
 */
export function useDescriptionFormatHints({
  simTokenData,
  simTokenInData,
  simSellTokenData,
  tokenInAddress,
  tokenInChainId,
  isNativeOnlyAction,
  chainId,
}: UseDescriptionFormatHintsParams): DescriptionFormatHints | undefined {
  return useMemo(() => {
    const token = simTokenData?.tokens?.[0];
    if (token) return { tokenDecimals: token.decimals, tokenSymbol: token.symbol };
    const tokenIn = simTokenInData?.tokens?.[0];
    if (tokenIn) return { tokenDecimals: tokenIn.decimals, tokenSymbol: tokenIn.symbol };
    const sellToken = simSellTokenData?.tokens?.[0];
    if (sellToken) return { tokenDecimals: sellToken.decimals, tokenSymbol: sellToken.symbol };
    if (isNativeTokenAddress(tokenInAddress) && tokenInChainId) {
      return {
        tokenDecimals: getNativeTokenDecimals(tokenInChainId),
        tokenSymbol: getChainInfo(tokenInChainId).gasToken,
      };
    }
    if (isNativeOnlyAction && chainId) {
      return { tokenDecimals: getNativeTokenDecimals(chainId), tokenSymbol: getChainInfo(chainId).gasToken };
    }
    return undefined;
  }, [simTokenData, simTokenInData, simSellTokenData, tokenInAddress, tokenInChainId, isNativeOnlyAction, chainId]);
}
